import { action, computed, makeAutoObservable, runInAction } from "mobx";
import UserStore from "../store/UserStore";
import UserVenuesStore from "../store/UserVenuesStore";
import apiClient from "../api/pe-api-client";

export type InjectableUserState = {
  userState?: UserState;
};

export type UserType = {
  userId: number;
  userName: string;

  firstName: string;
  lastName: string;
};

export type CommandCentreVenue = {
  venueId: number;
  displayName: string;
  iconUrl: string;
  backgroundUrl: string;

  venueFeatures: VenueFeatures;
  userVenuePermissions: UserPermissions;
};

export type VenueFeatures = {
  queryStringAttractors: boolean;
  draggableAttractors: boolean;
  mobileAttractorsByTier: boolean;
  mobileAboutItemAttachments: boolean;
  videoAttractorsEnabled: boolean;
};

export type UserPermissions = {
  isAdmin: boolean;
  hasAnalyticsAccess: boolean;
  hasHydraAccess: boolean;
};

export class UserState {
  isLoggedIn: boolean = false;

  // Becomes true once any cached session has been validated against the server
  // (or there was nothing cached to validate). Routing waits on this so the app
  // never renders authenticated UI off a stale/expired token. See verifyAuth().
  authChecked: boolean = false;

  user?: UserType = undefined;

  selectedVenue?: CommandCentreVenue = undefined;

  userVenues?: CommandCentreVenue[] = undefined;

  venueSearchTerm = "";

  loginInProgress = false;

  // Indicates a venue switch is in progress so UI can show a lightweight loader
  venueChangeInProgress = false;

  // Set by pages that hold unsaved edits (e.g. "3 unsaved geofence changes") so
  // the layout can confirm before a venue switch remounts the page.
  unsavedWork: string | null = null;

  constructor() {
    makeAutoObservable(this, { venueChangeTimer: false });
  }

  @computed get userVenuesAlphabetical() {
    // By id, not reference: refreshExtras() swaps in fresh venue objects while the selected
    // venue can still be one from the earlier list.
    let venues = this.userVenues?.filter(
      (venue) => venue.venueId !== this.selectedVenue?.venueId
    );

    if (this.venueSearchTerm !== undefined) {
      venues = venues?.filter((venue) =>
        venue.displayName.toLowerCase().includes(this.venueSearchTerm)
      );
    }

    return venues?.sort((venueOne, venueTwo) => {
      if (venueOne.displayName > venueTwo.displayName) {
        return 1;
      }

      if (venueOne.displayName < venueTwo.displayName) {
        return -1;
      }

      return 0;
    });
  }

  @computed get getVenueId() {
    return this.selectedVenue?.venueId;
  }

  @computed get isAdmin() {
    return this.user != undefined && this.selectedVenue?.venueId == 17;
  }

  @action
  login(user: UserType, userVenues: CommandCentreVenue[]) {
    this.user = user;
    this.userVenues = userVenues;

    if (this.userVenues?.length === 1) {
      this.selectedVenue = this.userVenues[0];
    } else if (!this.selectedVenue) {
      // A new tab or a refresh starts with no venue; reopen the one last picked here.
      this.selectedVenue = this.rememberedVenue(userVenues);
    }

    this.isLoggedIn = true;

    this.loginInProgress = false;
  }

  /**
   * The venue this browser last had selected, if the user can still access it. Returned from
   * the server's list rather than storage, so its features and permissions are current.
   */
  rememberedVenue(venues?: CommandCentreVenue[]) {
    const venueId = UserVenuesStore.getSelectedVenueId();
    if (venueId === null) return undefined;
    return venues?.find((venue) => venue.venueId === venueId);
  }

  @action
  setUnsavedWork(label: string | null) {
    this.unsavedWork = label;
  }

  changeVenue(venue: CommandCentreVenue) {
    runInAction(() => {
      this.selectedVenue = venue;
      this.venueSearchTerm = "";
    });
    UserVenuesStore.storeSelectedVenueId(venue.venueId);
  }

  // Minimum time the venue-switch loading state is shown, in ms.
  static readonly VENUE_CHANGE_LOADING_MS = 500;

  venueChangeTimer?: ReturnType<typeof setTimeout> = undefined;

  // Switch venue behind a loading state. The layout unmounts the current page while
  // venueChangeInProgress is true, then remounts it keyed by the new venue, so every
  // page re-initialises against the selected venue instead of patching stale state.
  changeVenueWithLoading(venue: CommandCentreVenue) {
    if (this.venueChangeTimer) {
      clearTimeout(this.venueChangeTimer);
    }

    // Enter the loading state and apply the venue change in one MobX transaction so
    // observers never see the loader without the new venue already in place.
    runInAction(() => {
      this.venueChangeInProgress = true;
      this.selectedVenue = venue;
      this.venueSearchTerm = "";
      this.unsavedWork = null;
    });
    UserVenuesStore.storeSelectedVenueId(venue.venueId);

    this.venueChangeTimer = setTimeout(() => {
      runInAction(() => {
        this.venueChangeInProgress = false;
      });
      this.venueChangeTimer = undefined;
    }, UserState.VENUE_CHANGE_LOADING_MS);
  }

  refreshExtras() {
    if (!this.isLoggedIn) return;

    apiClient
      .userExtras()
      .then((response: any) => {
        runInAction(() => {
          let responseUser = {
            userId: response.data.userIdInt,
            userName: response.data.userId,
            firstName: response.data.firstName,
            lastName: response.data.lastName,
          };

          UserStore.storeUser(responseUser);
          UserVenuesStore.storeVenues(response.data.commandCentreVenues);

          this.user = responseUser;
          this.userVenues = response.data.commandCentreVenues;

          if (this.userVenues?.length === 1) {
            this.selectedVenue = this.userVenues[0];
          }
        });
      })
      .catch((error: any) => {
        console.error(error);
      });
  }

  /**
   * Validate a cached session against the server before the app renders any
   * authenticated UI. The auth token lives in an HttpOnly cookie the frontend
   * cannot read, so the only way to know it is still valid is to ask the server.
   *
   *  - No cached user    -> nothing to verify; fall through to login.
   *  - Valid token       -> hydrate the user + venues from the server response.
   *  - Expired / invalid -> clear the stale cache so the route guards send the
   *                         user to login instead of flashing authed content.
   *
   * Routing waits on `authChecked` (see app.tsx), so this must always flip it to
   * true once the check settles.
   */
  verifyAuth() {
    const cachedUser = UserStore.getUser();

    if (!cachedUser || cachedUser.userId == null) {
      runInAction(() => {
        this.authChecked = true;
      });
      return;
    }

    // Seed the cached user up front: userExtras() builds its URL from the userId.
    runInAction(() => {
      this.user = cachedUser;
    });

    apiClient
      .userExtras()
      .then((response: any) => {
        // The axios interceptor resolves 401/403 to `undefined` (after forcing a
        // logout), so guard before trusting the response.
        if (response && response.data) {
          const responseUser: UserType = {
            userId: response.data.userIdInt,
            userName: response.data.userId,
            firstName: response.data.firstName,
            lastName: response.data.firstLast,
          };

          UserStore.storeUser(responseUser);
          UserVenuesStore.storeVenues(response.data.commandCentreVenues);

          this.login(responseUser, response.data.commandCentreVenues);
        } else {
          this.clearLocalSession();
        }
      })
      .catch(() => {
        this.clearLocalSession();
      })
      .finally(() => {
        runInAction(() => {
          this.authChecked = true;
        });
      });
  }

  clearLocalSession() {
    runInAction(() => {
      this.isLoggedIn = false;
      this.user = undefined;
      this.selectedVenue = undefined;
      this.userVenues = undefined;
      this.loginInProgress = false;
    });

    UserStore.clearStore();
    UserVenuesStore.clearStore();
  }

  logout() {
    this.clearLocalSession();
    apiClient.userLogout();
  }
}

export const userState = new UserState();
